import { Dayjs } from 'dayjs'
import { DateRangeValue } from '../../types/date/types'
import { FiscalYear } from '../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../types/periodGroup/PeriodGroup'
import { SortOrder } from '../../redux/context/filters/types'
import { ReportDataType } from '../../redux/context/reports/types'
import { parseDate } from '../../config/dayjsConfig'

export const getSuggestedNextPeriod = (fiscalYear?: FiscalYear): DateRangeValue<Dayjs> | undefined => {
  if (!fiscalYear) return undefined
  const startDate = parseDate(fiscalYear.startDate)?.add(1, 'years')
  const endDate = parseDate(fiscalYear.endDate)?.add(1, 'years')

  if (!startDate || !endDate) return undefined
  return [startDate, endDate]
}

const overlapsAny = (group: PeriodGroup | FiscalYear, fiscalYears: FiscalYear[]) => {
  const start = +new Date(group.startDate)
  const end = +new Date(group.endDate)
  return fiscalYears.some(fy => start <= +new Date(fy.endDate) && end >= +new Date(fy.startDate))
}

/**
 * Keeps period groups (fiscal years, Aikaväli ranges, quarters) that overlap a selected fiscal year.
 * Comparative groups (negative id) are kept together with their positive pair.
 */
export const filterGroupsBySelectedFiscalYears = <T extends PeriodGroup | FiscalYear>(
  groups: T[],
  selectedFiscalYears: FiscalYear[]
): T[] => {
  const keptIds = new Set(groups.filter(g => g.id > 0 && overlapsAny(g, selectedFiscalYears)).map(g => g.id))
  return groups.filter(g => (g.id > 0 ? keptIds.has(g.id) : keptIds.has(-g.id)))
}

/**
 * Reorders items by period blocks. Consecutive items sharing a key form a block (e.g. the
 * actuals/budget/cumulative columns of one month). 'desc' reverses the block order and keeps
 * the order inside each block. Input is expected in ascending order.
 */
export const orderPeriodBlocks = <T>(items: T[], keyOf: (item: T) => string | number, order: SortOrder): T[] => {
  if (order === 'asc') return items

  const blocks: T[][] = []
  items.forEach((item, index) => {
    if (index === 0 || keyOf(item) !== keyOf(items[index - 1])) {
      blocks.push([])
    }
    blocks[blocks.length - 1].push(item)
  })
  return blocks.reverse().flat()
}

/**
 * Whether a fiscal year produces any column at fiscal year level. Mirrors the column type rules in
 * useStatementTableColumns (filterColumnTypes) and useTableColumns (filteredPeriodGroupDataTypes):
 * future fiscal years have no actuals, and non-budgetable ones have no budget/forecast.
 */
export const fiscalYearHasColumns = (
  fiscalYear: FiscalYear,
  dataTypes: ReportDataType[],
  budgetCategoriesEnabled: boolean
): boolean => {
  const isFuture = +new Date(fiscalYear.startDate) > Date.now()
  const budgetDisabled = !budgetCategoriesEnabled || !fiscalYear.budgetable
  return dataTypes.some(dataType => {
    if (dataType === ReportDataType.comparativePeriod) return false
    if (dataType === ReportDataType.actuals) return !isFuture
    return !budgetDisabled
  })
}
